/**
 * Modal "Nuevo feriado" (008 · T075 · US4; contracts/ui.md §1.5): fecha, nombre y alcance ("Todas
 * las sedes" o una sede). Validación con `CreateHolidayBodySchema`. Errores de la API al campo:
 * `409 CONFLICT` (`field: 'day'`, ya hay uno activo ese día para ese alcance) y `409 PERIOD_CLOSED`
 * en la fecha; el resto en un toast.
 */
import { useMemo, useState } from 'react';

import { CreateHolidayBodySchema, type Site } from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { scheduleErrorMessage, useCreateHoliday } from '../api';
import { formatLocalDay } from '../format';

import type { ApiError } from '../../../api/client';

/** Centinela del alcance "Todas las sedes" (`site_id = null`). */
export const COMPANY_SCOPE = 'COMPANY';

type HolidayField = 'day' | 'name' | 'site_id';

interface HolidayFormValues {
  day: string;
  name: string;
  scope: string;
}

/** Errores de la API con campo (duplicado y período cerrado van a la fecha). */
export function holidayApiErrors(error: ApiError): Partial<Record<HolidayField, string>> {
  if (error.is('CONFLICT') && error.details?.['field'] === 'day') {
    return { day: t('schedules.holidays.form.errors.dayTaken') };
  }
  if (error.is('PERIOD_CLOSED')) {
    return { day: scheduleErrorMessage(error) };
  }
  const issues = error.details?.['issues'];
  const errors: Partial<Record<HolidayField, string>> = {};
  if (error.is('VALIDATION_ERROR') && Array.isArray(issues)) {
    for (const issue of issues as { path?: unknown; message?: unknown }[]) {
      const path = typeof issue.path === 'string' ? issue.path.split('.')[0] : undefined;
      if (
        (path === 'day' || path === 'name' || path === 'site_id') &&
        typeof issue.message === 'string'
      ) {
        errors[path] ??= issue.message;
      }
    }
  }
  return errors;
}

export interface HolidayFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sites: readonly Site[];
  /** Fecha inicial (hoy en la zona de la empresa, o un día del año elegido). */
  defaultDay: string;
}

/** El formulario se monta al abrir: cada apertura empieza limpia. */
export function HolidayFormDialog({
  open,
  onOpenChange,
  sites,
  defaultDay,
}: HolidayFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? (
        <HolidayFormContent onOpenChange={onOpenChange} sites={sites} defaultDay={defaultDay} />
      ) : null}
    </Dialog>
  );
}

function HolidayFormContent({
  onOpenChange,
  sites,
  defaultDay,
}: Omit<HolidayFormDialogProps, 'open'>) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const create = useCreateHoliday();
  const [values, setValues] = useState<HolidayFormValues>({
    day: defaultDay,
    name: '',
    scope: COMPANY_SCOPE,
  });
  const [errors, setErrors] = useState<Partial<Record<HolidayField, string>>>({});

  const scopeOptions = useMemo(
    () => [
      { value: COMPANY_SCOPE, label: t('schedules.holidays.scope.company') },
      ...sites.map((site) => ({ value: site.id, label: site.name })),
    ],
    [sites],
  );

  function submit() {
    const parsed = CreateHolidayBodySchema.safeParse({
      day: values.day,
      name: values.name,
      site_id: values.scope === COMPANY_SCOPE ? null : values.scope,
    });
    if (!parsed.success) {
      const next: Partial<Record<HolidayField, string>> = {};
      for (const issue of parsed.error.issues) {
        const field = String(issue.path[0] ?? '');
        if (field === 'day' || field === 'name' || field === 'site_id') {
          next[field] ??= t(`schedules.holidays.form.errors.${field}`);
        }
      }
      setErrors(next);
      return;
    }
    setErrors({});
    create.mutate(parsed.data, {
      onSuccess: (saved) => {
        toast({
          title: t('schedules.holidays.toast.created', {
            name: saved.name,
            day: formatLocalDay(saved.day),
          }),
          variant: 'success',
        });
        onOpenChange(false);
      },
      onError: (error) => {
        const fieldErrors = holidayApiErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
        toast({ title: scheduleErrorMessage(error), variant: 'error' });
      },
    });
  }

  return (
    <DialogContent
      title={t('schedules.holidays.form.title')}
      description={t('schedules.holidays.form.description')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            type="submit"
            form="holiday-form"
            loading={create.isPending}
            loadingText={t('common.states.saving')}
            {...readOnlyProps(readOnly)}
          >
            {t('schedules.holidays.form.submit')}
          </Button>
        </>
      }
    >
      <form
        id="holiday-form"
        data-testid="holiday-form"
        className="flex flex-col gap-fm-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field label={t('schedules.holidays.form.day')} required error={errors.day}>
          {(control) => (
            <Input
              {...control}
              type="date"
              value={values.day}
              onChange={(event) => setValues((v) => ({ ...v, day: event.target.value }))}
            />
          )}
        </Field>
        <Field label={t('schedules.holidays.form.name')} required error={errors.name}>
          {(control) => (
            <Input
              {...control}
              autoComplete="off"
              maxLength={80}
              value={values.name}
              onChange={(event) => setValues((v) => ({ ...v, name: event.target.value }))}
            />
          )}
        </Field>
        <Field
          label={t('schedules.holidays.form.scope')}
          required
          help={t('schedules.holidays.form.scopeHelp')}
          error={errors.site_id}
        >
          {(control) => (
            <Select
              id={control.id}
              aria-describedby={control['aria-describedby']}
              value={values.scope}
              onValueChange={(scope) => setValues((v) => ({ ...v, scope }))}
              options={scopeOptions}
            />
          )}
        </Field>
      </form>
    </DialogContent>
  );
}
